/**
 * Agent Contract Review Exception Resolution Page
 * 
 * Displays low-confidence AI classifications from contract analysis
 * Allows users to review and resolve product, channel, and territory mapping exceptions
 * 
 * Features:
 * - Exception table display with confidence scoring
 * - Three action types: Accept suggestion, Map to existing category, Create new category
 * - Real-time hierarchy preview for new category creation
 * - Interactive modals for mapping and creation workflows
 * - Agent insights panel showing semantic similarity reasoning
 * - Breadcrumb navigation and contract metadata display
 * 
 * Exception Types:
 * - Product Categories: Product mappings below confidence threshold
 * - Sales Channels: Sales channel mappings below threshold
 * - Territories: Geographic territory mappings below threshold
 * 
 * State Management:
 * - contract: Current contract metadata (id, name, licensor, exception counts)
 * - productItems/channelItems/territoryItems: Exception items by category
 * - selectedItem: Currently selected exception for insight panel
 * - mapModalOpen/createModalOpen: Modal visibility states
 * - taxonomy/fullTaxonomy: Taxonomy data for mapping dropdowns
 * - loading: API data fetch state
 * 
 * API Integration:
 * - GET agent/contract/{contractId}/exceptions/: Fetch contract exceptions
 * - GET /taxonomy/: Fetch full taxonomy structure (L1, L2, L3)
 * - POST agent/exceptions/resolve/: Save mapping resolution (accept/map/create)
 * 
 * Confidence Scoring:
 * - < 0.4 (40%): Red text - Very low confidence, needs review
 * - 0.4 - 0.6: Orange text - Medium confidence
 * - > 0.6: Dark text - Higher confidence
 * 
 * Dependencies:
 * - react-bootstrap: UI components (Modal, Form, Table, etc.)
 * - lucide-react: Icons (ChevronRight, Check, Info)
 * - config_mapping: API response codes
 * - server_utils: API communication functions
 * - config_ip_details: API server URL
 * 
 * @component
 * @returns {JSX.Element} Contract review page with exception resolution interface
 * 
 * @example
 * // Usage in router with contract ID parameter
 * <Route path="/agent-contract-review/:contractId" element={<ContractReview />} />
 */

// ContractReview.bootstrap.tsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  Badge,
  Table,
  Form,
  Modal,
  Button,
  Accordion,
  InputGroup,
  ListGroup,
} from "react-bootstrap";
import { ChevronRight, ChevronDown, Check, Info } from "lucide-react";
import { API_SUCCESS_RESPONSE_CODE } from "../../utils/config_mapping";
import { apiCall } from "../../utils/server_utils";
import { API_SERVER_URL } from "../../utils/config_ip_details";
// import { useToast } from "@/hooks/use-toast";
import HeaderLogo  from "../structure/headerlogo";

/**
 * TypeScript Type Definitions (Reference Only)
 * 
 * This is a .js file but uses TypeScript-style comments for documentation
 * 
 * @typedef {Object} LowConfidenceItem
 * @property {string} id - Unique identifier for the exception item
 * @property {string} extractedTerm - Original text extracted from contract
 * @property {string} suggestedMapping - AI-suggested category mapping
 * @property {string} [mappingHierarchy] - Hierarchy path (e.g., "Level1 > Level2 > Level3")
 * @property {number} confidence - Confidence score (0.0 - 1.0)
 * @property {string} status - Item status: "PENDING_REVIEW" or "RESOLVED"
 * @property {string} [resolvedMapping] - Final resolved mapping if status is RESOLVED
 * 
 * @typedef {Object} ContractData
 * @property {string} id - Contract ID
 * @property {string} name - Contract name/title
 * @property {string} licensorName - Name of licensor/counterparty
 * @property {number} productExceptions - Count of product exceptions
 * @property {number} channelExceptions - Count of channel exceptions
 * @property {number} territoryExceptions - Count of territory exceptions
 * @property {number} threshold - Confidence threshold for exceptions (0.0 - 1.0)
 * @property {LowConfidenceItem[]} productItems - Product category exceptions
 * @property {LowConfidenceItem[]} channelItems - Sales channel exceptions
 * @property {LowConfidenceItem[]} territoryItems - Territory exceptions
 * 
 * @typedef {Object} TaxonomyEntry
 * @property {string} id - Unique identifier
 * @property {string} label - Display name
 * @property {number} level - Hierarchy level (1, 2, or 3)
 * @property {string} [parentId] - Parent entry ID
 */

/* -------------------------
   MOCK DATA - Development/Testing Data
   Provides sample contract and taxonomy data for component development
   In production, this data is fetched from the API
   ------------------------- */

/**
 * Mock Contract Data
 * Sample contract with product, channel, and territory exceptions
 * Used when API calls fail or during development
 * 
 * @constant {ContractData} mockContract
 */
const mockContract = {
  id: "CTR-2024-001",
  name: "Contract Name 1",
  licensorName: "Licensor Name 1",
  productExceptions: 3,
  channelExceptions: 1,
  territoryExceptions: 0,
  threshold: 0.65,
  productItems: [
    {
      id: "p1",
      extractedTerm: "Enterprise Cloud Platform",
      suggestedMapping: "Cloud Infrastructure",
      mappingHierarchy: "Software > Infrastructure > Cloud Infrastructure",
      confidence: 0.42,
      status: "PENDING_REVIEW",
    },
    {
      id: "p2",
      extractedTerm: "AI Analytics Suite",
      suggestedMapping: "Analytics Software",
      mappingHierarchy: "Software > Business Intelligence > Analytics Software",
      confidence: 0.58,
      status: "PENDING_REVIEW",
    },
    {
      id: "p3",
      extractedTerm: "Custom Integration Services",
      suggestedMapping: "Professional Services",
      mappingHierarchy: "Services > Consulting > Professional Services",
      confidence: 0.51,
      status: "PENDING_REVIEW",
    },
  ],
  channelItems: [
    {
      id: "c1",
      extractedTerm: "Reseller Network Distribution",
      suggestedMapping: "Partner Channel",
      confidence: 0.38,
      status: "PENDING_REVIEW",
    },
  ],
  territoryItems: [],
};

/**
 * Mock Taxonomy Data
 * Sample taxonomy structure for L3 categories (flattened for display)
 * In production, this comes from the API with full L1, L2, L3 hierarchy
 * 
 * @constant {Object} mockTaxonomy
 * @property {string[]} products - L3 product categories
 * @property {string[]} channels - L3 sales channel categories
 * @property {string[]} territories - L3 territory categories
 */
const mockTaxonomy = {
  products: [
    "Cloud Infrastructure",
    "Analytics Software",
    "Professional Services",
    "Database Systems",
    "Security Software",
  ],
  channels: ["Direct Sales", "Partner Channel", "Online", "Enterprise"],
  territories: ["North America", "EMEA", "APAC", "LATAM"],
};

/**
 * ContractReview Component
 * 
 * Main component for reviewing and resolving AI-identified contract exceptions
 * Provides interface for manual review of low-confidence AI classifications
 * 
 * Features:
 * - Accordion-based organization of exception types (Products, Channels, Territories)
 * - Interactive action dropdown for each exception item
 * - Modal workflows for mapping and creating new categories
 * - Agent insights panel with semantic similarity explanations
 * - Real-time hierarchy preview during category creation
 * 
 * User Workflows:
 * 1. Accept: User accepts AI suggestion without modification
 * 2. Map: User selects existing taxonomy category for the exception
 * 3. Create: User creates new L3 category under selected parent hierarchy
 * 
 * @function ContractReview
 * @returns {JSX.Element} Contract review page with exception tables and modals
 */
const ContractReview = () => {
  // ===== ROUTER HOOKS =====
  const { contractId } = useParams(); // Get contract ID from URL parameter
  const navigate = useNavigate(); // Navigation to other routes
  // const { toast } = useToast(); // Toast notification hook (currently disabled)

  // ===== STATE: Contract Data =====
  const [contract, setContract] = useState(mockContract); // Current contract metadata
  const [productItems, setProductItems] = useState([]); // Product category exceptions
  const [channelItems, setChannelItems] = useState([]); // Sales channel exceptions
  const [territoryItems, setTerritoryItems] = useState([]); // Territory exceptions

  // ===== STATE: UI - Selection & Modal Visibility =====
  const [selectedItem, setSelectedItem] = useState(null); // Currently selected exception for insights panel
  const [productOpen, setProductOpen] = useState(true); // Product accordion expansion state
  const [channelOpen, setChannelOpen] = useState(true); // Channel accordion expansion state
  const [territoryOpen, setTerritoryOpen] = useState(true); // Territory accordion expansion state
  const [showAll, setShowAll] = useState(false); // Show resolved items toggle (currently unused)

  // ===== STATE: Modals =====
  const [mapModalOpen, setMapModalOpen] = useState(false); // "Map to Existing" modal visibility
  const [createModalOpen, setCreateModalOpen] = useState(false); // "Create New Category" modal visibility

  // ===== STATE: Modal Form Values - Map Modal =====
  const [searchTerm, setSearchTerm] = useState(""); // Search input for category selection
  const [currentGroup, setCurrentGroup] = useState("products"); // Current category group (products/channels/territories)

  // ===== STATE: Modal Form Values - Create Modal =====
  const [newCategoryName, setNewCategoryName] = useState(""); // New category L3 name input
  const [newCategoryDescription, setNewCategoryDescription] = useState(""); // Optional description for new category
  const [selectedParentL1, setSelectedParentL1] = useState(""); // Parent L1 category selection
  const [selectedParentL2, setSelectedParentL2] = useState(""); // Parent L2 category selection

  // ===== STATE: Taxonomy & Loading =====
  const [taxonomy, setTaxonomy] = useState(mockTaxonomy); // L3 categories (flattened list for dropdowns)
  const [fullTaxonomy, setFullTaxonomy] = useState({ products: [], channels: [], territories: [] }); // Full L1/L2/L3 hierarchy
  const [loading, setLoading] = useState(true); // Data loading state

  /**
   * Initialize Component - Fetch Contract & Taxonomy Data
   * 
   * Called on mount with contractId as dependency
   * Simultaneously fetches contract exceptions and taxonomy structure
   * 
   * @effect
   * @dependency {string} contractId - Contract ID from URL parameter
   */
  // Fetch contract details on mount
  useEffect(() => {
    if (contractId) {
      fetchContractDetails();
      fetchTaxonomy();
    }
  }, [contractId]);

  /**
   * Fetch Contract Exception Details from API
   * 
   * Retrieves low-confidence exceptions identified by AI for this contract
   * Separates exceptions into product, channel, and territory items
   * 
   * API Endpoint: GET agent/contract/{contractId}/exceptions/
   * 
   * Process:
   * 1. Set loading state to true
   * 2. Call API with contract ID
   * 3. Extract contract metadata (name, licensor, type, threshold)
   * 4. Populate separate state arrays for each exception type
   * 5. Handle errors gracefully
   * 
   * Response Data:
   * - id, name, licensorName: Contract identification
   * - threshold: Confidence threshold used for exceptions
   * - productItems, channelItems, territoryItems: Exception arrays
   * 
   * @async
   * @function fetchContractDetails
   * @returns {Promise<void>}
   */
  const fetchContractDetails = async () => {
    try {
      setLoading(true);
      const response = await apiCall("GET", API_SERVER_URL + `agent/contract/${contractId}/exceptions/`);
      
      console.log("Contract Exception Details Response:", response);
      
      if (response.code === API_SUCCESS_RESPONSE_CODE && response.data) {
        const data = response.data;
        
        // Set contract metadata from API response
        setContract({
          id: data.id || contractId,
          name: data.name || "Contract Name",
          licensorName: data.licensorName || "Licensor",
          contractType: data.contractType || "Contract",
          threshold: data.threshold || 0.65,
          productExceptions: data.productExceptions || 0,
          channelExceptions: data.channelExceptions || 0,
          territoryExceptions: data.territoryExceptions || 0,
        });
        
        // Populate exception items from API (each is a LowConfidenceItem)
        setProductItems(data.productItems || []);
        setChannelItems(data.channelItems || []);
        setTerritoryItems(data.territoryItems || []);
      }
    } catch (error) {
      console.error("Error fetching contract details:", error);
      alert("Error loading contract details: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Fetch Full Taxonomy Structure from API
   * 
   * Retrieves complete taxonomy hierarchy for category mapping and creation
   * Stores both flattened (L3 only) and full (L1/L2/L3) versions
   * 
   * API Endpoint: GET /taxonomy/
   * 
   * Process:
   * 1. Call API to fetch taxonomy structure
   * 2. Store full hierarchy in fullTaxonomy state (for parent selection)
   * 3. Extract L3 items and flatten into taxonomy state (for dropdowns)
   * 4. Handle missing data gracefully
   * 
   * Response Structure:
   * - product_taxonomies: Array of product category hierarchy
   * - sales_taxonomies: Array of sales channel hierarchy
   * - territories_taxonomies: Array of territory hierarchy
   * - Each taxonomy entry has: id, label, level (1/2/3), parentId
   * 
   * @async
   * @function fetchTaxonomy
   * @returns {Promise<void>}
   */
  const fetchTaxonomy = async () => {
    try {
      const response = await apiCall("GET", API_SERVER_URL + "/taxonomy/");
      
      console.log("Taxonomy Response:", response);
      
      if (response.code === API_SUCCESS_RESPONSE_CODE && response.data) {
        // Store full taxonomy structure with L1, L2, L3 for parent selection
        setFullTaxonomy({
          products: response.data.product_taxonomies || [],
          channels: response.data.sales_taxonomies || [],
          territories: response.data.territories_taxonomies || [],
        });

        // Extract L3 categories only (leaf level) for mapping dropdowns
        const productL3 = response.data.product_taxonomies
          ?.filter(item => item.level === 3)
          .map(item => item.label) || [];
        
        const channelL3 = response.data.sales_taxonomies
          ?.filter(item => item.level === 3)
          .map(item => item.label) || [];
        
        const territoryL3 = response.data.territories_taxonomies
          ?.filter(item => item.level === 3)
          .map(item => item.label) || [];

        // Set flattened taxonomy for dropdown display
        setTaxonomy({
          products: productL3,
          channels: channelL3,
          territories: territoryL3,
        });
      }
    } catch (error) {
      console.error("Error fetching taxonomy:", error);
    }
  };

  /* ========================================================================================
     EVENT HANDLERS & ACTION METHODS
     ======================================================================================== */

  /**
   * Handle Action Selection from Dropdown
   * 
   * Routes user action to appropriate handler (accept/map/create)
   * Sets modal visibility and initializes form states
   * 
   * Actions:
   * - "accept": Accept AI suggestion without modification
   * - "map": Open modal to map to existing taxonomy category
   * - "create": Open modal to create new taxonomy category
   * 
   * @function handleActionSelect
   * @param {LowConfidenceItem} item - Exception item being actioned
   * @param {string} action - Action type (accept/map/create)
   * @param {string} group - Category group (products/channels/territories)
   */
  const handleActionSelect = (
    item,
    action,
    group
  ) => {
    // Set selected item and category for subsequent operations
    setSelectedItem(item);
    setCurrentGroup(group);

    if (action === "accept") {
      // Accept suggestion: directly apply suggested mapping
      handleAcceptSuggestion(item, group);
    } else if (action === "map") {
      // Map action: open modal for existing category selection
      setMapModalOpen(true);
      setSearchTerm(""); // Clear previous search
    } else if (action === "create") {
      // Create action: open modal for new category creation
      setCreateModalOpen(true);
      setSelectedParentL1(""); // Clear previous selections
      setSelectedParentL2("");
      setNewCategoryName("");
      setNewCategoryDescription("");
    }
  };

  /**
   * Accept AI Suggestion - Apply Without Modification
   * 
   * User accepts the AI-suggested mapping directly
   * Marks item as RESOLVED with suggested mapping
   * Saves to API and updates UI state
   * 
   * Process:
   * 1. Create updated item with RESOLVED status
   * 2. Call saveMapping API with "accept" action
   * 3. Update item in appropriate state array
   * 4. Check if all exceptions are now resolved
   * 
   * @async
   * @function handleAcceptSuggestion
   * @param {LowConfidenceItem} item - Exception item to accept
   * @param {string} group - Category group (products/channels/territories)
   * @returns {Promise<void>}
   */
  const handleAcceptSuggestion = async (
    item,
    group
  ) => {
    // Create updated item with resolved status
    const updatedItem = {
      ...item,
      status: "RESOLVED",
      resolvedMapping: item.suggestedMapping,
    };
    
    // Save mapping and update UI
    await saveMapping(item.id, item.suggestedMapping, group, "accept");
    updateItems(group, item.id, updatedItem);
    checkCompletion();
  };

  /**
   * Map to Existing Taxonomy Category
   * 
   * User selects existing L3 category from taxonomy for mapping
   * Looks up complete hierarchy (L1, L2, L3) for the selected item
   * Saves mapping with full hierarchy information
   * 
   * Process:
   * 1. Validate search term is not empty
   * 2. Find full L3 entry in taxonomy
   * 3. Look up parent L2 entry via parentId
   * 4. Look up parent L1 entry via parentId
   * 5. Build hierarchy data object
   * 6. Call saveMapping with hierarchy
   * 7. Update item and close modal
   * 
   * @async
   * @function handleMapToExisting
   * @returns {Promise<void>}
   */
  const handleMapToExisting = async () => {
    if (!selectedItem) return;
    if (!searchTerm) return;

    // Get full taxonomy for current group to find hierarchy
    const fullTax = fullTaxonomy[currentGroup] || [];
    const l3Item = fullTax.find(t => t.label === searchTerm && t.level === 3);
    
    if (!l3Item) {
      alert('Invalid category selection');
      return;
    }

    // Look up parent L2 via parentId
    const l2Item = fullTax.find(t => t.id === l3Item.parentId);
    // Look up parent L1 via parentId
    const l1Item = l2Item ? fullTax.find(t => t.id === l2Item.parentId) : null;

    if (!l1Item || !l2Item) {
      alert('Unable to find parent categories');
      return;
    }

    // Create updated item with resolved status
    const updatedItem = {
      ...selectedItem,
      status: "RESOLVED",
      resolvedMapping: searchTerm,
    };
    
    // Build hierarchy data for API
    const hierarchyData = {
      l1: l1Item.label,
      l2: l2Item.label,
      l3: l3Item.label
    };
    
    // Save mapping with hierarchy and update UI
    await saveMapping(selectedItem.id, searchTerm, currentGroup, "map", "", hierarchyData);
    updateItems(currentGroup, selectedItem.id, updatedItem);
    setMapModalOpen(false);
    checkCompletion();
  };

  /**
   * Create New L3 Category and Apply Mapping
   * 
   * User creates new taxonomy L3 category under selected parent hierarchy
   * Validates parent selections (L1, L2) and new category name
   * Saves creation to API and applies mapping immediately
   * 
   * Process:
   * 1. Validate required fields (name, L1, L2)
   * 2. Create updated item with resolved status
   * 3. Build additionalData with parent info
   * 4. Call saveMapping with "create" action
   * 5. Update item, close modal, reset form
   * 6. Check if all exceptions resolved
   * 
   * @async
   * @function handleCreateNew
   * @returns {Promise<void>}
   */
  const handleCreateNew = async () => {
    if (!selectedItem || !newCategoryName || !selectedParentL1 || !selectedParentL2) return;

    // Create updated item with resolved status
    const updatedItem = {
      ...selectedItem,
      status: "RESOLVED",
      resolvedMapping: newCategoryName,
    };

    // Include parent IDs in the create action for API
    const additionalData = {
      parentL1: selectedParentL1,
      parentL2: selectedParentL2,
      level: 3
    };

    // Save mapping and update UI
    await saveMapping(selectedItem.id, newCategoryName, currentGroup, "create", newCategoryDescription, additionalData);
    updateItems(currentGroup, selectedItem.id, updatedItem);
    setCreateModalOpen(false);
    
    // Reset form fields after successful creation
    setSelectedParentL1("");
    setSelectedParentL2("");
    setNewCategoryName("");
    setNewCategoryDescription("");
    
    checkCompletion();
  };

  /**
   * Save Mapping to API
   * 
   * Persists exception resolution to backend
   * Supports three action types with different payloads
   * 
   * API Endpoint: POST agent/exceptions/resolve/
   * 
   * Action-Specific Payloads:
   * - "accept": Just use resolvedMapping
   * - "map": Include l1, l2, l3 hierarchy from additionalData
   * - "create": Include parentL1, parentL2, new l3 name, level, and optional description
   * 
   * Process:
   * 1. Build payload based on action type
   * 2. Add common fields (contractId, itemId, group, action)
   * 3. Call API POST with payload
   * 4. Log response and return success/failure
   * 
   * @async
   * @function saveMapping
   * @param {string} itemId - Exception item ID
   * @param {string} mapping - Mapping value (suggestion or new category name)
   * @param {string} group - Category group (products/channels/territories)
   * @param {string} action - Action type (accept/map/create)
   * @param {string} [description=""] - Optional description for new categories
   * @param {Object} [additionalData={}] - Action-specific data (hierarchy or parents)
   * @returns {Promise<boolean>} True if save successful, false otherwise
   */
  const saveMapping = async (itemId, mapping, group, action, description = "", additionalData = {}) => {
    try {
      // Build base payload with common fields
      let payload = {
        contractId: contractId,
        itemId: itemId,
        group: group,
        action: action
      };

      // Build payload based on action type
      if (action === "map") {
        // For map action, use l1, l2, l3 from additionalData (hierarchy lookup)
        payload.l1 = additionalData.l1;
        payload.l2 = additionalData.l2;
        payload.l3 = additionalData.l3;
      } else if (action === "create") {
        // For create action, include parent information and new category details
        payload.l1 = additionalData.parentL1;
        payload.l2 = additionalData.parentL2;
        payload.l3 = mapping; // New category name
        payload.level = additionalData.level;
        if (description) {
          payload.description = description;
        }
      } else if (action === "accept") {
        // For accept action, just use resolvedMapping
        payload.resolvedMapping = mapping;
      }

      console.log("Saving Mapping:", payload);

      // Call API to save mapping
      const response = await apiCall("POST", API_SERVER_URL + "agent/exceptions/resolve/", payload);
      
      console.log("Save Mapping Response:", response);

      if (response.code === API_SUCCESS_RESPONSE_CODE) {
        // Success - mapping saved successfully
        return true;
      } else {
        alert("Failed to save mapping. Please try again.");
        return false;
      }
    } catch (error) {
      console.error("Error saving mapping:", error);
      alert("Error saving mapping: " + error.message);
      return false;
    }
  };

  /**
   * Update Item in State Array
   * 
   * Finds and updates a specific exception item in the appropriate state array
   * Also updates selectedItem if it's the item being modified
   * 
   * @function updateItems
   * @param {string} group - Category group (products/channels/territories)
   * @param {string} itemId - Item ID to update
   * @param {LowConfidenceItem} updatedItem - Updated item data
   */
  const updateItems = (
    group,
    itemId,
    updatedItem
  ) => {
    // Update appropriate array based on group
    if (group === "products") {
      setProductItems((prev) => prev.map((it) => (it.id === itemId ? updatedItem : it)));
      // if the selectedItem was the one updated, reflect it in insights panel
      if (selectedItem?.id === itemId) setSelectedItem(updatedItem);
    } else if (group === "channels") {
      setChannelItems((prev) => prev.map((it) => (it.id === itemId ? updatedItem : it)));
      if (selectedItem?.id === itemId) setSelectedItem(updatedItem);
    } else {
      setTerritoryItems((prev) => prev.map((it) => (it.id === itemId ? updatedItem : it)));
      if (selectedItem?.id === itemId) setSelectedItem(updatedItem);
    }
  };

  /**
   * Check Completion Status
   * 
   * Verifies if all exceptions have been resolved
   * Shows completion notification if all items resolved
   * 
   * @function checkCompletion
   */
  const checkCompletion = () => {
    // Combine all exceptions from all categories
    const allItems = [...productItems, ...channelItems, ...territoryItems];
    // Filter for items still pending review
    const pendingItems = allItems.filter((item) => item.status === "PENDING_REVIEW");

    if (pendingItems.length === 0) {
      // All items resolved - show completion message
    //   toast({
    //     title: "All low confidence mappings for this contract have been resolved.",
    //   });
    }
  };

  /**
   * Get Taxonomy Options for Modal Dropdown
   * 
   * Returns filtered list of categories based on search term
   * Used in "Map to Existing Category" modal
   * 
   * @function getTaxonomyOptions
   * @returns {string[]} Filtered list of L3 category names
   */
  const getTaxonomyOptions = () => {
    const options = taxonomy[currentGroup] || [];
    // Filter by search term if provided
    if (!searchTerm) return options;
    return options.filter((opt) => opt.toLowerCase().includes(searchTerm.toLowerCase()));
  };

  /**
   * Get Hierarchy Path for Taxonomy Item
   * 
   * Looks up full hierarchy path (L1 > L2 > L3) for a given L3 label
   * Used to display context path in taxonomy dropdown
   * 
   * Process:
   * 1. Find L3 item by label
   * 2. Look up parent L2 via parentId
   * 3. Look up parent L1 via parentId
   * 4. Return formatted hierarchy string
   * 
   * @function getHierarchyPath
   * @param {string} label - L3 category name
   * @returns {string} Formatted hierarchy path or just label if not found
   * 
   * @example
   * getHierarchyPath("Cloud Infrastructure")
   * // Returns: "Software → Infrastructure → Cloud Infrastructure"
   */
  const getHierarchyPath = (label) => {
    // Get full taxonomy for current group
    const fullTax = fullTaxonomy[currentGroup] || [];
    // Find the L3 item by label
    const item = fullTax.find(t => t.label === label && t.level === 3);
    if (!item) return label;

    // Look up parent L2 and L1
    const l2 = fullTax.find(t => t.id === item.parentId);
    const l1 = l2 ? fullTax.find(t => t.id === l2.parentId) : null;

    // Format hierarchy if all parents found
    if (l1 && l2) {
      return `${l1.label} → ${l2.label} → ${label}`;
    }
    return label;
  };

  /* ========================================================================================
     TABLE RENDERING - Exception Items Display
     ======================================================================================== */

  /**
   * Render Exception Items Table
   * 
   * Renders Bootstrap Table with exception items for a specific category
   * Shows extracted term, AI suggestion, confidence score, and action controls
   * 
   * Features:
   * - Responsive table with row selection
   * - Color-coded confidence scores (danger/warning/dark)
   * - Action dropdown for pending items (disabled for resolved)
   * - Resolved badge and checkmark indicator
   * - Text wrapping for long extracted terms
   * 
   * Confidence Coloring:
   * - < 0.4 (40%): Red text (text-danger) - Very low confidence
   * - 0.4 - 0.6: Orange text (text-warning) - Medium confidence
   * - >= 0.6: Dark text (text-dark) - Higher confidence
   * 
   * Table Columns:
   * 1. Extracted term: Original text from contract
   * 2. Agent suggestion: AI-suggested mapping with hierarchy
   * 3. Confidence: Score from 0.00 to 1.00
   * 4. Action: Dropdown (pending) or RESOLVED badge
   * 5. Status icon: Checkmark if resolved
   * 
   * @function renderExceptionTable
   * @param {LowConfidenceItem[]} items - Exception items to display
   * @param {string} group - Category group (products/channels/territories)
   * @returns {JSX.Element|null} Bootstrap Table or null if no items
   */
  const renderExceptionTable = (
    items,
    group
  ) => {
    // Return null if no items to display
    if (!items || items.length === 0) return null;

    return (
      <Table hover responsive>
        {/* Table header with column titles */}
        <thead>
          <tr>
            <th style={{ maxWidth: '250px', width: '250px' }}>Extracted term</th>
            <th>Agent suggestion</th>
            <th>Confidence</th>
            <th>Action</th>
            <th></th>
          </tr>
        </thead>

        {/* Table body with exception items */}
        <tbody>
          {items.map((item) => {
            // Determine if this is the currently selected item
            const isSelected = selectedItem?.id === item.id;
            
            // Determine confidence color class based on score
            const confidenceClass =
              item.confidence < 0.4 ? "text-danger fw-semibold" :
              item.confidence < 0.6 ? "text-warning fw-semibold" :
              "text-dark";

            return (
              <tr
                key={item.id}
                className={isSelected ? "table-active" : undefined}
                onClick={() => setSelectedItem(item)}
                style={{ cursor: "pointer" }}
              >
                {/* Extracted term - Original contract text */}
                <td className="align-middle" style={{ maxWidth: '250px' }}>
                  <div 
                    className="fw-medium" 
                    style={{ 
                      wordWrap: 'break-word',
                      whiteSpace: 'normal',
                      overflowWrap: 'break-word'
                    }}
                  >
                    {item.extractedTerm}
                  </div>
                </td>

                {/* Agent suggestion - AI recommended mapping and hierarchy */}
                <td className="align-middle">
                  <div className="fw-medium">{item.suggestedMapping}</div>
                  {item.mappingHierarchy && (
                    <div className="text-muted small">{item.mappingHierarchy}</div>
                  )}
                </td>

                {/* Confidence score - Color-coded by score */}
                <td className="align-middle">
                  <span className={confidenceClass}>{item.confidence.toFixed(2)}</span>
                </td>

                {/* Action - Dropdown for pending, badge for resolved */}
                <td className="align-middle" onClick={(e) => e.stopPropagation()}>
                  {item.status === "RESOLVED" ? (
                    // Resolved status badge
                   <Badge bg="primary">{item.status}</Badge>
                  ) : (
                    // Action selection dropdown for pending items
                    <Form.Select
                      aria-label="Select action"
                      size="sm"
                      onChange={(e) => handleActionSelect(item, e.target.value, group)}
                      defaultValue=""
                      style={{ width: 260 }}
                    >
                      <option value="" disabled>
                        Select action
                      </option>
                      <option value="accept">Accept suggested match</option>
                      <option value="map">Map to existing category</option>
                      <option value="create">Create new L3 category</option>
                    </Form.Select>
                  )}
                </td>

                {/* Status icon - Checkmark for resolved items */}
                <td className="align-middle text-center">
                  {item.status === "RESOLVED" && <Check size={18} className="text-success" />}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    );
  };

  /* ========================================================================================
     COMPONENT RENDER - Main JSX Layout
     ======================================================================================== */

  return (
    <div className="main-content" style={{ paddingTop: 64 }}>
       <HeaderLogo />
      <div className="page-content">
        <div className="container-fluid">
          {/* Page header section */}
          <div id="table_height">
            <div className="flex-grow-1" id="row_heading">
              {/* Page title */}
              <div className="page-title">Contract Review – Agent Assist
</div>
              
              {/* Loading spinner - shown while data is fetching */}
              {loading && (
                <div className="text-center py-3">
                  <div className="spinner-border text-primary spinner-border-sm me-2" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <span className="text-muted small">Loading contract details...</span>
                </div>
              )}
              
              {/* Explanation text */}
              <p className="text-muted my-2 ff-secondary">
The Agent has pre-classified this contract. Only low confidence Product, Channel and Territory mappings are shown here for your review.
              </p>
            </div>

              {/* Logo badge */}
              <div className="ms-auto bg-primary" style={{padding:"1px",border:"1px solid #000",position:"absolute", right:"5px",borderRadius:"2px",top:"15px"}}>
    <img src="../images/predatica_logo.png" alt="Logo" height={"20px"}  /> 
    </div>
    
            {/* Error message container */}
            <div id="app_err"></div>
          </div>
   
        {/* Navigation breadcrumb (currently commented out) */}

        {/* Header block with contract info and badges */}
        <Row className="align-items-start mb-3">
          <Col md={8}>
            {/* Breadcrumb / Back link */}
              <ol className="breadcrumb bg-white p-2 rounded">
    <span onClick={() => navigate("/agent-routed-exceptions")}   style={{ color: 'blue', cursor: 'pointer' }}
>
        ← Back 
      </span> &nbsp;&nbsp;
   
 
            {/* Contract name and licensor */}
            <div className="text-muted">
              {contract.name} • {contract.licensorName}
            </div>

             </ol>
          </Col>

          {/* Exception count badges */}
          <Col md={4} className="text-md-end mt-3 mt-md-0">
            <div className="d-flex justify-content-end flex-wrap gap-2">
              {/* Product exceptions badge - shown if count > 0 */}
              {contract.productExceptions > 0 && (
                <Badge bg="secondary">Product exceptions: {contract.productExceptions}</Badge>
              )}
              {/* Channel exceptions badge - shown if count > 0 */}
              {contract.channelExceptions > 0 && (
                <Badge bg="secondary">Channel exceptions: {contract.channelExceptions}</Badge>
              )}
              {/* Territory exceptions badge - shown if count > 0 */}
              {contract.territoryExceptions > 0 && (
                <Badge bg="secondary">Territory exceptions: {contract.territoryExceptions}</Badge>
              )}
              {/* Confidence threshold used for exception flagging */}
              <Badge bg="light" text="dark">Review threshold: {(contract.threshold * 100).toFixed(0)}%</Badge>
            </div>
          </Col>
        </Row>

        {/* Tools and toggles row */}
        <Row className="mb-4">
          <Col>
            <div className="d-flex justify-content-between align-items-center">
              <div className="text-muted small">
                {/* Additional instructions can go here */}
              </div>

              {/* Show all toggle (currently hidden with d-none class) */}
              <div className="d-flex align-items-center gap-2 d-none">
                <Form.Check
                  type="switch"
                  id="show-all"
                  label="Show all"
                  checked={showAll}
                  onChange={(e) => setShowAll(e.currentTarget.checked)}
                />
              </div>
            </div>
          </Col>
        </Row>

        {/* Two-column layout - Exceptions tables (left) and insights (right) */}
        <Row className="g-4">
          {/* LEFT COLUMN - Exception tables with accordions */}
          <Col lg={8}>
            <Accordion defaultActiveKey={productOpen ? "0" : undefined}>
              {/* ===== PRODUCTS SECTION ===== */}
              {productItems.length > 0 && (
                <Card className="mb-3">
                  <Accordion.Item eventKey="0">
                    <Accordion.Header>
                      <div className="d-flex align-items-center justify-content-between w-100">
                        <span>Product Categories</span>
                      </div>
                    </Accordion.Header>
                    <Accordion.Body>
                      {renderExceptionTable(productItems, "products")}
                    </Accordion.Body>
                  </Accordion.Item>
                </Card>
              )}

              {/* ===== CHANNELS SECTION ===== */}
              {channelItems.length > 0 && (
                <Card className="mb-3">
                  <Accordion.Item eventKey="1">
                    <Accordion.Header>
                      <span>Sales Channels</span>
                    </Accordion.Header>
                    <Accordion.Body>
                      {renderExceptionTable(channelItems, "channels")}
                    </Accordion.Body>
                  </Accordion.Item>
                </Card>
              )}

              {/* ===== TERRITORIES SECTION ===== */}
              {territoryItems.length > 0 && (
                <Card className="mb-3">
                  <Accordion.Item eventKey="2">
                    <Accordion.Header>
                      <span>Territories</span>
                    </Accordion.Header>
                    <Accordion.Body>
                      {renderExceptionTable(territoryItems, "territories")}
                    </Accordion.Body>
                  </Accordion.Item>
                </Card>
              )}
            </Accordion>
          </Col>

          {/* RIGHT COLUMN - Agent insights panel (sticky) */}
          <Col lg={4}>
            <Card className="p-3 sticky-top" style={{ top: "1rem" }}>
              {/* Insights header */}
              <div className="d-flex align-items-center mb-3">
                <Info size={18} className="me-2 text-primary" />
                <h6 className="mb-0">Agent Insights</h6>
              </div>

              {/* Insights content - shows details for selected item */}
              {selectedItem ? (
                <>
                  {/* Extracted term from contract */}
                  <div className="mb-3">
                    <div className="text-muted small">Extracted term</div>
                    <div className="fw-medium">{selectedItem.extractedTerm}</div>
                  </div>

                  {/* AI suggested mapping */}
                  <div className="mb-3">
                    <div className="text-muted small">Suggested category</div>
                    <div className="fw-medium">{selectedItem.suggestedMapping}</div>
                  </div>

                  {/* Confidence score as percentage */}
                  <div className="mb-3">
                    <div className="text-muted small">Confidence</div>
                    <div className="fw-medium">{(selectedItem.confidence * 100).toFixed(0)}%</div>
                  </div>

                  {/* Explanation of why this suggestion was made */}
                  <div className="border-top pt-3">
                    <div className="text-muted small mb-1">Why this suggestion</div>
                    <div className="small">
                      The Agent found semantic similarity between the extracted term and existing taxonomy entries. Low confidence may be due to ambiguous wording or multiple potential matches.
                    </div>
                  </div>
                </>
              ) : (
                // Placeholder text when no item selected
                <div className="small text-muted">
                  Select a row from the tables to view Agent insights and recommendations.
                </div>
              )}
            </Card>
          </Col>
        </Row>
   

        {/* ========================================================================================
             MODAL 1: Map to Existing Category
             ======================================================================================== */}
      <Modal show={mapModalOpen} onHide={() => setMapModalOpen(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Map to Existing Category</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {/* Display selected extracted term */}
          <div className="mb-2">
            <div className="text-muted small">Extracted term</div>
            <div className="fw-medium">{selectedItem?.extractedTerm}</div>
          </div>

          {/* Search box for category selection */}
          <Form.Group className="mb-3" controlId="search">
            <Form.Label>Search existing categories</Form.Label>
            <Form.Control
              placeholder="Type to search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Form.Group>

          {/* List of matching categories with hierarchy path */}
          <div style={{ maxHeight: 300, overflowY: "auto" }}>
            <ListGroup variant="flush">
              {getTaxonomyOptions().map((option) => (
                <ListGroup.Item
                  key={option}
                  action
                  active={searchTerm === option}
                  onClick={() => setSearchTerm(option)}
                  className="d-flex flex-column align-items-start"
                >
                  {/* Category name (L3) */}
                  <div className="fw-medium">{option}</div>
                  {/* Full hierarchy path (L1 → L2 → L3) */}
                  <div className="text-muted small">{getHierarchyPath(option)}</div>
                </ListGroup.Item>
              ))}
              {getTaxonomyOptions().length === 0 && (
                <div className="text-muted small p-2">No results</div>
              )}
            </ListGroup>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setMapModalOpen(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleMapToExisting} disabled={!searchTerm}>
            Save Mapping
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ========================================================================================
           MODAL 2: Create New L3 Category
           ======================================================================================== */}
      <Modal show={createModalOpen} onHide={() => setCreateModalOpen(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Create New L3 Category</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {/* Display selected extracted term */}
          <div className="mb-3 p-3 bg-light rounded">
            <div className="text-muted small">Extracted term</div>
            <div className="fw-medium">{selectedItem?.extractedTerm}</div>
          </div>

          {/* ===== PARENT L1 SELECTION ===== */}
          <Form.Group className="mb-3" controlId="parentL1">
            <Form.Label>
              Parent Category (L1) <span className="text-danger">*</span>
            </Form.Label>
            <Form.Select
              value={selectedParentL1}
              onChange={(e) => {
                setSelectedParentL1(e.target.value);
                setSelectedParentL2(""); // Reset L2 when L1 changes
              }}
            >
              <option value="">Select L1 Category...</option>
              {fullTaxonomy[currentGroup]
                ?.filter(item => item.level === 1)
                .map(item => (
                  <option key={item.id} value={item.id}>
                    {item.label}
                  </option>
                ))}
            </Form.Select>
          </Form.Group>

          {/* ===== PARENT L2 SELECTION ===== */}
          {selectedParentL1 && (
            <Form.Group className="mb-3" controlId="parentL2">
              <Form.Label>
                Parent Subcategory (L2) <span className="text-danger">*</span>
              </Form.Label>
              <Form.Select
                value={selectedParentL2}
                onChange={(e) => setSelectedParentL2(e.target.value)}
              >
                <option value="">Select L2 Category...</option>
                {fullTaxonomy[currentGroup]
                  ?.filter(item => item.level === 2 && item.parentId === selectedParentL1)
                  .map(item => (
                    <option key={item.id} value={item.id}>
                      {item.label}
                    </option>
                  ))}
              </Form.Select>
            </Form.Group>
          )}

          {/* ===== NEW CATEGORY NAME ===== */}
          <Form.Group className="mb-3" controlId="name">
            <Form.Label>
              New L3 Category Name <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              placeholder="Enter category name"
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
            />
            <Form.Text className="text-muted">
              This will be added as a Level 3 category under the selected parent.
            </Form.Text>
          </Form.Group>

          {/* ===== HIERARCHY PREVIEW ===== */}
          {selectedParentL1 && selectedParentL2 && newCategoryName && (
            <div className="mb-3 p-3 border rounded bg-light">
              <div className="text-muted small mb-1">Hierarchy Preview:</div>
              <div className="d-flex align-items-center gap-2">
                <Badge bg="primary">
                  {fullTaxonomy[currentGroup]?.find(i => i.id === selectedParentL1)?.label}
                </Badge>
                <span>→</span>
                <Badge bg="info">
                  {fullTaxonomy[currentGroup]?.find(i => i.id === selectedParentL2)?.label}
                </Badge>
                <span>→</span>
                <Badge bg="success">{newCategoryName}</Badge>
              </div>
            </div>
          )}

          {/* ===== OPTIONAL DESCRIPTION ===== */}
          <Form.Group className="mb-3" controlId="description">
            <Form.Label>Description (optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Enter category description"
              value={newCategoryDescription}
              onChange={(e) => setNewCategoryDescription(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => {
            setCreateModalOpen(false);
            setSelectedParentL1("");
            setSelectedParentL2("");
            setNewCategoryName("");
            setNewCategoryDescription("");
          }}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleCreateNew} 
            disabled={!newCategoryName || !selectedParentL1 || !selectedParentL2}
          >
            Create & Apply
          </Button>
        </Modal.Footer>
      </Modal>
    </div></div></div>
  );
};

/**
 * Export Default: ContractReview Component
 * 
 * Enables default import syntax:
 * import ContractReview from './pages/agentcontractreview'
 */
export default ContractReview;
